import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    H6Emphasized,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import type {
    DbosSummaryByWorkflow,
    DbosSummaryResponse,
} from '../types/DbosTypes';
import { DBOS_STATUS_COLORS } from './dbosConstants';

interface DbosWorkflowSummaryProps {
    data: DbosSummaryResponse;
}

interface SummaryCard {
    label: string;
    value: number;
    color: string;
    bgColor: string;
}

export const DbosWorkflowSummary = ({ data }: DbosWorkflowSummaryProps) => {
    const [showBreakdown, setShowBreakdown] = useState(false);

    const totalWorkflows = Object.values(data.totals).reduce(
        (sum, count) => sum + count,
        0,
    );

    const successRate =
        totalWorkflows > 0
            ? ((data.totals.SUCCESS / totalWorkflows) * 100).toFixed(1)
            : '0.0';

    const cards: SummaryCard[] = [
        {
            label: 'Total',
            value: totalWorkflows,
            color: '#333333',
            bgColor: '#f5f5f5',
        },
        {
            label: 'Success',
            value: data.totals.SUCCESS,
            color: '#28a745',
            bgColor: '#e8f5e9',
        },
        {
            label: 'Pending',
            value: data.totals.PENDING,
            color: '#ffc107',
            bgColor: '#fff8e1',
        },
        {
            label: 'Error',
            value: data.totals.ERROR,
            color: '#dc3545',
            bgColor: data.totals.ERROR > 0 ? '#ffebee' : '#f5f5f5',
        },
        {
            label: 'Max Recovery',
            value: data.totals.MAX_RECOVERY_ATTEMPTS_EXCEEDED,
            color: '#e65100',
            bgColor:
                data.totals.MAX_RECOVERY_ATTEMPTS_EXCEEDED > 0
                    ? '#fff3e0'
                    : '#f5f5f5',
        },
        {
            label: 'Recovered',
            value: data.totalRecoveredCount,
            color: '#1565c0',
            bgColor: '#e3f2fd',
        },
    ];

    const countsBodyTemplate = (
        rowData: DbosSummaryByWorkflow,
        field: string,
    ) => {
        const value = rowData.counts[field as keyof typeof rowData.counts] ?? 0;
        const color =
            DBOS_STATUS_COLORS[field as keyof typeof DBOS_STATUS_COLORS] ??
            '#000';
        return (
            <span
                style={{
                    color: value > 0 ? color : '#ccc',
                    fontWeight: value > 0 ? 'bold' : 'normal',
                }}
            >
                {value}
            </span>
        );
    };

    return (
        <YStack gap={16} width="100%">
            <XStack
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <H6Emphasized>Workflow Status Overview</H6Emphasized>
                <BodySmallRegular color="$gray1000">
                    Success Rate: {successRate}%
                </BodySmallRegular>
            </XStack>

            {/* Summary Cards Row */}
            <XStack gap={12} flexWrap="wrap" width="100%">
                {cards.map((card) => (
                    <YStack
                        key={card.label}
                        alignItems="center"
                        justifyContent="center"
                        padding={16}
                        borderRadius={12}
                        flex={1}
                        minWidth={120}
                        style={{ backgroundColor: card.bgColor }}
                    >
                        <BodyMediumEmphasized style={{ color: card.color }}>
                            {card.value}
                        </BodyMediumEmphasized>
                        <BodySmallRegular color="$gray1000">
                            {card.label}
                        </BodySmallRegular>
                    </YStack>
                ))}
            </XStack>

            {/* Expandable Per-Workflow Breakdown */}
            {data.byWorkflow.length > 0 && (
                <YStack gap={8} width="100%">
                    <Button
                        variant="tertiary"
                        size="small"
                        onPress={() => {
                            setShowBreakdown(!showBreakdown);
                        }}
                    >
                        {showBreakdown
                            ? 'Hide Breakdown'
                            : `Show Breakdown (${data.byWorkflow.length} workflow types)`}
                    </Button>

                    {showBreakdown && (
                        <DataTable
                            value={data.byWorkflow}
                            size="small"
                            tableStyle={{
                                width: '100%',
                                fontSize: '0.875rem',
                            }}
                        >
                            <Column
                                field="workflowName"
                                header="Workflow"
                                style={{ fontSize: '0.75rem' }}
                            />
                            <Column
                                field="configName"
                                header="Config"
                                style={{ fontSize: '0.75rem' }}
                            />
                            <Column
                                header="Pending"
                                alignHeader="right"
                                bodyStyle={{ textAlign: 'right' }}
                                body={(row: DbosSummaryByWorkflow) =>
                                    countsBodyTemplate(row, 'PENDING')
                                }
                            />
                            <Column
                                header="Success"
                                alignHeader="right"
                                bodyStyle={{ textAlign: 'right' }}
                                body={(row: DbosSummaryByWorkflow) =>
                                    countsBodyTemplate(row, 'SUCCESS')
                                }
                            />
                            <Column
                                header="Error"
                                alignHeader="right"
                                bodyStyle={{ textAlign: 'right' }}
                                body={(row: DbosSummaryByWorkflow) =>
                                    countsBodyTemplate(row, 'ERROR')
                                }
                            />
                            <Column
                                header="Max Recovery"
                                alignHeader="right"
                                bodyStyle={{ textAlign: 'right' }}
                                body={(row: DbosSummaryByWorkflow) =>
                                    countsBodyTemplate(
                                        row,
                                        'MAX_RECOVERY_ATTEMPTS_EXCEEDED',
                                    )
                                }
                            />
                            <Column
                                header="Cancelled"
                                alignHeader="right"
                                bodyStyle={{ textAlign: 'right' }}
                                body={(row: DbosSummaryByWorkflow) =>
                                    countsBodyTemplate(row, 'CANCELLED')
                                }
                            />
                            <Column
                                header="Enqueued"
                                alignHeader="right"
                                bodyStyle={{ textAlign: 'right' }}
                                body={(row: DbosSummaryByWorkflow) =>
                                    countsBodyTemplate(row, 'ENQUEUED')
                                }
                            />
                            <Column
                                field="recoveredCount"
                                header="Recovered"
                                alignHeader="right"
                                bodyStyle={{ textAlign: 'right' }}
                            />
                        </DataTable>
                    )}
                </YStack>
            )}
        </YStack>
    );
};
